/**
 * Zod runtime schemas for the chat-agent SSE event payloads.
 *
 * Phase 4.1.5 — drop-in validation at the most-fragile boundary in the app.
 * The TypeScript types in innoq.ts describe what the backend *should* send;
 * these schemas verify what it *did* send before that data flows into the
 * store. On schema mismatch the event is dropped with a `console.warn` —
 * better than rendering with `undefined` fields and causing a hard crash.
 *
 * Routine REST endpoints are deliberately NOT covered here (cost > benefit
 * per the task's own scope note).
 */

import { z } from 'zod';

// ── Primitive shapes ─────────────────────────────────────────────────────────

// Two shapes coexist:
//   list   → { type:'list',  page, filters?, label }
//   entity → { type:'entity', url,            label }
// `page` is required for list pills (drives the route) but absent on entity
// pills (the URL is self-contained). Marking it optional prevents Zod from
// rejecting the entire `items` array when an entity pill is present —
// which used to silently swallow every pill from a single-result lookup
// like a dossier query.
export const FilterSuggestionSchema = z.object({
  label: z.string(),
  page: z.string().optional(),
  filters: z.record(z.string(), z.unknown()).optional(),
  type: z.enum(['list', 'entity', 'stats']).optional(),
  url: z.string().optional(),
}).passthrough();

export const CitationSchema = z.object({
  id: z.number(),
  label: z.string(),
  url: z.string(),
  type: z.enum(['councillor', 'affair', 'dispatch', 'speech']).optional(),
  // Bare bill number for affair/dispatch citations → rendered as a bill button.
  ref: z.string().optional(),
  // Councillor-only portrait url → rendered as an avatar (never a number).
  // Lenient/optional like the rest of the citation shape.
  portrait: z.string().nullable().optional(),
}).passthrough();

// People-by-name safety net: the full set of councillors the turn gathered,
// as {name, url}. Matched against the rendered answer text so a councillor the
// model NAMED but forgot to cite ([N]) still gets a profile link. Lenient
// (passthrough) like the other event schemas.
export const LinkablePersonSchema = z.object({
  name: z.string(),
  url: z.string(),
  portrait: z.string().nullable().optional(),
}).passthrough();

export const ToolCallInfoSchema = z.object({
  name: z.string(),
  id: z.string(),
}).passthrough();

export const DebugInfoSchema = z.object({
  iteration: z.number(),
  model_duration_ms: z.number(),
  tool_duration_ms: z.number(),
  tool_calls: z.array(z.object({
    id: z.string(),
    name: z.string(),
    args: z.record(z.string(), z.unknown()),
    result_preview: z.string(),
  }).passthrough()),
}).passthrough();

export const DoneInfoSchema = z.object({
  session_id: z.string(),
  iterations_capped: z.boolean().optional(),
  // The id of the assistant message persisted for this turn. Optional —
  // the cap-warning paths and other edge-case `done` emissions don't carry
  // one, in which case the frontend won't surface a feedback affordance
  // for that turn.
  message_id: z.string().optional(),
}).passthrough();

export const ChatErrorInfoSchema = z.object({
  message: z.string(),
  code: z.string(),
}).passthrough();

// ── Event-frame envelopes ────────────────────────────────────────────────────

export const TextDeltaPayloadSchema      = z.object({ delta: z.string() }).passthrough();
export const FilterSuggestionsPayloadSchema = z.object({ items: z.array(FilterSuggestionSchema) }).passthrough();
export const FollowUpsPayloadSchema      = z.object({ items: z.array(z.string()) }).passthrough();
export const CitationsPayloadSchema      = z.object({ items: z.array(CitationSchema) }).passthrough();
export const LinkablePeoplePayloadSchema = z.object({ items: z.array(LinkablePersonSchema) }).passthrough();

/**
 * Validate a parsed SSE payload for a given event name.
 * Returns the typed payload on success; `null` on schema mismatch (and logs
 * a warning so drift between backend and frontend surfaces in the console).
 */
export function validateChatEvent<T>(
  event: string,
  payload: unknown,
  schema: z.ZodType<T>,
): T | null {
  const result = schema.safeParse(payload);
  if (result.success) return result.data;
  console.warn(`[chat-sse] dropped malformed "${event}" event`, result.error.issues);
  return null;
}

// ── Phase 4 analytics endpoints ──────────────────────────────────────────────
// New endpoints introduced in Phase 4 are validated at fetch sites so a
// schema drift surfaces as a soft warning + empty fallback rather than a
// hard render crash. Existing list endpoints are deliberately not covered
// here (see task 4.1.5 — the maintenance tax isn't worth it everywhere).

export const TopicCloudSchema = z.array(z.object({
  word:  z.string(),
  count: z.number(),
}));

export type TopicCloudItem = z.infer<typeof TopicCloudSchema>['0'];
